import PropTypes from 'prop-types';

/**
 * @typedef {Object} ProductData
 * @property {boolean} [bestSeller] - Whether product is marked as best seller
 * @property {string} [brand] - The brand name of the product
 * @property {boolean} [coldPacked] - Whether the product is a cold pack
 * @property {boolean} [containsDrinksItem] - Whether product contains drinks
 * @property {boolean} [exitSku] - Whether product is being discontinued
 * @property {boolean} [isBundle] - Whether product is an M+ bundle (portion-based)
 * @property {boolean} [isGift] - Whether this is a free gift product
 * @property {number} [msrp] - Manufacturer's suggested retail price
 * @property {string} name - The display name of the product
 * @property {boolean} [newProduct] - Whether product is newly added
 * @property {string} photoMed - URL of the product's medium-sized photo
 * @property {number} [plusMemberPointPrice] - Plus member point price of the product
 * @property {number} [plusMemberPrice] - Plus member price of the product
 * @property {boolean} [plusMemberPriceEligible] - Whether the product is eligible for plus member pricing
 * @property {number} [portion] - Bundle quantity (e.g. 2 for "2 for $X.XX")
 * @property {number} [pointPrice] - Point price of the product
 * @property {number} price - Regular price of the product
 * @property {boolean} [sale] - Whether product is on sale
 * @property {boolean} [soldOut] - Whether the product is sold out
 * @property {string} [sourcingReason] - Reason for sourcing this product
 */

/**
 * @typedef {Object} ProductMetaData
 * @property {number} [aisleID] - ID of the aisle
 * @property {number} [carouselID] - ID of the carousel
 * @property {string} [carouselName] - Name of the carousel
 * @property {boolean} [carouselHorizontalScroll] - Whether carousel uses horizontal scroll
 * @property {boolean} [customAisle] - Whether this is a custom aisle
 * @property {boolean} [isCartUpsell] - Whether this card is in a cart upsell
 * @property {boolean} [searchRecommendation] - Whether this is a search recommendation
 * @property {boolean} [isCarouselModalCard] - Whether this card is in a carousel modal
 * @property {boolean} [isBanner] - Whether this card is from a banner
 * @property {string} [upSellsSource] - Source identifier for upsell products (e.g., 'cart_upsell', 'pdp_upsell', 'plp_upsell')
 */

export const PRODUCT_DATA_PROP_TYPE = PropTypes.shape({
    bestSeller: PropTypes.bool,
    brand: PropTypes.string,
    coldPacked: PropTypes.bool,
    containsDrinksItem: PropTypes.bool,
    exitSku: PropTypes.bool,
    isBundle: PropTypes.bool,
    isGift: PropTypes.bool,
    isPerk: PropTypes.bool,
    msrp: PropTypes.number,
    name: PropTypes.string.isRequired,
    newProduct: PropTypes.bool,
    photoMed: PropTypes.string.isRequired,
    plusMemberPointPrice: PropTypes.number,
    plusMemberPrice: PropTypes.number,
    plusMemberPriceEligible: PropTypes.bool,
    pointPrice: PropTypes.number,
    portion: PropTypes.number,
    price: PropTypes.number.isRequired,
    sale: PropTypes.bool,
    soldOut: PropTypes.bool,
    sourcingReason: PropTypes.string,
    variant: PropTypes.oneOf(['default', 'perk', 'perk-redeemed']),
});

export const PRODUCT_META_DATA_PROP_TYPE = PropTypes.shape({
    aisleID: PropTypes.number,
    carouselHorizontalScroll: PropTypes.bool,
    carouselID: PropTypes.number,
    carouselName: PropTypes.string,
    customAisle: PropTypes.bool,
    isBanner: PropTypes.bool,
    isCarouselModalCard: PropTypes.bool,
    isCartUpsell: PropTypes.bool,
    searchRecommendation: PropTypes.bool,
    upSellsSource: PropTypes.string,
});
